<script lang="ts" setup>
import type { ContentLink } from '~/models/Content/ContentLink'
import { useI18n } from 'vue-i18n'
import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { setTitle } from '~/utils/dom'
import useLicenseControlStore from '~/stores/licenseControl'
import useAuthStore from '~/stores/auth'
import { Subtree } from '~/models/Content/Subtree'
import { ContentType } from '~/models/Content/ContentType'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useContentApi from '~/api/contentApi'
import TopBar from '~/components/utils/TopBar.vue'
import Button from '~/components/ui/Button.vue'

const props = defineProps<{ locationId: number }>()

const { t } = useI18n()
const { findContents } = useContentApi()
const { buildResourceUrl } = useUrlBuilder()
const { checkItemLicense } = useLicenseControlStore()
const { isAuthenticated } = storeToRefs(useAuthStore())

const { data } = useQuery({
  queryKey: computed(() => ['link-page', props.locationId]),
  queryFn: () => findContents<ContentLink>({
    subtreeCriterion: [Subtree.Content, Subtree.Media],
    contentTypeCriterion: [ContentType.Link],
    locationIdCriterion: [Number(props.locationId)],
  }).then(([content]) => content),
  staleTime: Infinity,
  retry: 1,
})

const linkUrl = computed(() => data.value && isAuthenticated.value ? buildResourceUrl(data.value) : null)

const linkHost = computed(() => {
  if (!linkUrl.value)
    return ''
  try {
    return new URL(linkUrl.value).hostname
  } catch (error) {
    console.error('Error parsing URL:', error)
    return ''
  }
})

watch(data, (value) => {
  if (!value)
    return
  setTitle(value.title)
  checkItemLicense(value)
}, { immediate: true })
</script>

<template>
  <div class="flex h-screen">
    <TopBar
      v-if="data"
      :item="data"
    />
    <div class="m-auto">
      <Button
        v-if="data && linkUrl"
        :href="linkUrl"
        size="lg"
        target="_tab"
      >
        {{ t('visitIn', { in: `«${data.title}»` }) }}
      </Button>
      <p
        v-if="linkHost"
        class="text-gray-40 mt-2 text-center text-sm"
        v-text="linkHost"
      />
    </div>
  </div>
</template>
